import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  ClockIcon,
  FolderOpenIcon,
  PackageXIcon,
  PercentIcon,
} from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { SummaryCard } from "@/shared/ui/SummaryCard";
import type { DisputeListItem } from "../api/client";
import { useDisputesQuery, useDisputesSummaryQuery } from "../api/queries";
import { DisputesFilter } from "../ui/DisputesFilter";
import { DisputesTable } from "../ui/DisputesTable";
import { DisputesToolbar } from "../ui/DisputesToolbar";

export function DisputesPage() {
  const nav = useNavigate({ from: "/app/disputes" });
  const search = useSearch({ from: "/app/disputes" }) as OffsetPageQueryDTO;

  const summary = useDisputesSummaryQuery();
  const summaryData = summary.data ?? {
    responseTimeAvgHours: null,
    openCases: 0,
    resolutionRatePercent: 0,
    blockedOrders: 0,
  };

  const list = useDisputesQuery({
    limit: search.limit ?? 20,
    offset: search.offset ?? 0,
    sort: search.sort,
    filters: search.filters,
  });

  const data = list.data?.data ?? [];
  const meta = list.data?.meta ?? { total: 0, limit: 20, offset: 0 };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຂໍ້ພິພາດ" },
          ]}
        />
      </Header>

      <Main>
        <DisputesToolbar />

        <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryCard
            variant="kpi"
            title="ເວລາຕອບສະໜອງ (ສະເລ່ຍ)"
            icon={ClockIcon}
            color="primary"
            items={[
              {
                label: "",
                value:
                  summaryData.responseTimeAvgHours != null
                    ? `${Number(summaryData.responseTimeAvgHours.toFixed(1))} ຊົ່ວໂມງ`
                    : "—",
              },
            ]}
            subtitle="ເວລາເລີ່ມຈາກເປີດຂໍ້ພິພາດຈົນຕັດສິນ"
          />
          <SummaryCard
            variant="kpi"
            title="ກໍລະນີທີ່ເປີດຢູ່"
            icon={FolderOpenIcon}
            color="orange"
            items={[
              { label: "", value: summaryData.openCases.toLocaleString() },
            ]}
            subtitle="ຂໍ້ພິພາດທີ່ຍັງບໍ່ທັນຕັດສິນ (ເປີດ ຫຼື ກຳລັງກວດສອບ)"
          />
          <SummaryCard
            variant="kpi"
            title="ອັດຕາການຕັດສິນ"
            icon={PercentIcon}
            color="success"
            items={[
              { label: "", value: `${summaryData.resolutionRatePercent}%` },
            ]}
            subtitle="ສັດຈະຕັດສິນແລ້ວ (ຕັດສິນ ຫຼື ປະຕິເສດ) ຕໍ່ທັງໝົດ"
          />
          <SummaryCard
            variant="kpi"
            title="ອໍເດີທີ່ຖືກຂັດຂວາງ"
            icon={PackageXIcon}
            color="destructive"
            items={[
              { label: "", value: summaryData.blockedOrders.toLocaleString() },
            ]}
            subtitle="ອໍເດີທີ່ສະຖານະ disputed"
          />
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <DisputesFilter />

          <DisputesTable
            isLoading={list.isLoading}
            data={data}
            offset={meta.offset}
            limit={meta.limit}
            totalCount={meta.total}
            sortBy={search.sort?.[0]?.field}
            sortOrder={search.sort?.[0]?.dir}
            onView={(row: DisputeListItem) =>
              nav({
                to: "/app/disputes/$id",
                params: { id: row.id },
              })
            }
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  offset: 0,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
          />
        </div>
      </Main>
    </>
  );
}
